import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function TitlesSystem({ playerId }) {
  const [titles, setTitles] = useState([]);
  const [ownedDogs, setOwnedDogs] = useState([]);
  const [pendingTitles, setPendingTitles] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [dogId, setDogId] = useState('');
  const [selectedSport, setSelectedSport] = useState('');
  const [selectedTitleId, setSelectedTitleId] = useState('');
  const [proofUrls, setProofUrls] = useState(['']);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!playerId) return;

    const fetchData = async () => {
      try {
        const [titlesRes, dogsRes, pendingRes, invRes] = await Promise.all([
          fetch(`${API_BASE_URL}/title-definitions`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }),
          fetch(`${API_BASE_URL}/my-dogs/${playerId}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }),
          fetch(`${API_BASE_URL}/my-pending-titles/${playerId}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }),
          fetch(`${API_BASE_URL}/my-inventory/${playerId}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })
        ]);

        const errors = [];
        if (!titlesRes.ok) errors.push(`Title definitions failed: ${titlesRes.status}`);
        if (!dogsRes.ok) errors.push(`My dogs failed: ${dogsRes.status}`);
        if (!pendingRes.ok) errors.push(`Pending titles failed: ${pendingRes.status}`);
        if (!invRes.ok) errors.push(`Inventory failed: ${invRes.status}`);
        if (errors.length > 0) throw new Error(errors.join('; '));

        const [titlesData, dogsData, pendingData, invData] = await Promise.all([
          titlesRes.json(),
          dogsRes.json(),
          pendingRes.json(),
          invRes.json()
        ]);
        setTitles(titlesData);
        setOwnedDogs(dogsData);
        setPendingTitles(pendingData);
        setInventory(invData.items || []);
        if (dogsData.length > 0) setDogId(dogsData[0].dogId);
      } catch (error) {
        setMessage('Error: ' + error.message);
      }
    };
    fetchData();
  }, [playerId]);

  useEffect(() => {
    if (selectedTitleId) {
      const selectedTitle = titles.find(t => t._id === selectedTitleId);
      const reqLinks = getRequirements(selectedTitle)?.requiredArtworkLinks || 1;
      setProofUrls(prev => {
        if (prev.length === reqLinks) return prev;
        const newUrls = Array(reqLinks).fill('');
        prev.forEach((url, i) => {
          if (i < reqLinks) newUrls[i] = url;
        });
        return newUrls;
      });
    } else {
      setProofUrls(['']);
    }
  }, [selectedTitleId, titles]);

  const submitTitle = async () => {
    const selectedTitle = titles.find(t => t._id === selectedTitleId);
    const reqLinks = getRequirements(selectedTitle)?.requiredArtworkLinks || 0;
    const reducedLinks = selectedItemIds.reduce((sum, id) => sum + (inventory.find(i => i.itemId === id)?.titleModifiers?.reduceArtworkLinks || 0), 0);
    const neededLinks = Math.max(0, reqLinks - reducedLinks);

    const reqQS = getRequirements(selectedTitle)?.requiredQS || 0;
    const reducedQS = selectedItemIds.reduce((sum, id) => {
      const item = inventory.find(i => i.itemId === id);
      const modifiers = item?.titleModifiers || {};
      const sportMatch = !modifiers.sport || modifiers.sport === selectedTitle?.category;
      return sum + (sportMatch ? (modifiers.reduceQS || 0) : 0);
    }, 0);
    const neededQS = Math.max(0, reqQS - reducedQS);

    const validProofUrls = proofUrls.slice(0, neededLinks).filter(url => url && url.trim() !== '');
    if (!dogId || !selectedTitleId) {
      setMessage('Please select a dog and title.');
      return;
    }
    if (neededLinks > 0 && validProofUrls.length < neededLinks) {
      setMessage(`Error: Need ${neededLinks} artwork link${neededLinks > 1 ? 's' : ''}. Provided: ${validProofUrls.length}`);
      return;
    }
    if (selectedDog.totalUsableQualifyingScores < neededQS) {
      setMessage(`Not enough QS: Need ${neededQS}, have ${selectedDog.totalUsableQualifyingScores}`);
      return;
    }

    const payload = { 
      playerId, 
      dogId, 
      titleId: selectedTitleId,
      artworkUrls: validProofUrls.length > 0 ? validProofUrls : [],
      usedItemIds: selectedItemIds,
      effectiveQS: neededQS
    };

    try {
      const res = await fetch(`${API_BASE_URL}/title-submit`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}` 
        },
        body: JSON.stringify(payload)
      });
      const text = await res.text();
      if (!res.ok) throw new Error(text);
      const data = JSON.parse(text);
      setMessage(data.message);
      setDogId(ownedDogs[0]?.dogId || '');
      setSelectedSport('');
      setSelectedTitleId('');
      setProofUrls(['']);
      setSelectedItemIds([]);
      const [pendingRes, dogsRes, invRes] = await Promise.all([
        fetch(`${API_BASE_URL}/my-pending-titles/${playerId}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }),
        fetch(`${API_BASE_URL}/my-dogs/${playerId}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }),
        fetch(`${API_BASE_URL}/my-inventory/${playerId}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })
      ]);
      setPendingTitles(await pendingRes.json());
      setOwnedDogs(await dogsRes.json());
      setInventory(await invRes.json().items || []);
    } catch (error) {
      setMessage('Error submitting title: ' + error.message);
    }
  };

  const clearDeniedTitle = async (titleId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/clear-denied-title`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}` 
        },
        body: JSON.stringify({ playerId, titleId })
      });
      if (!res.ok) throw new Error('Clear failed');
      const data = await res.json();
      setPendingTitles(pendingTitles.filter(t => t._id !== titleId));
      setMessage(data.message);
      const invRes = await fetch(`${API_BASE_URL}/my-inventory/${playerId}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      setInventory(await invRes.json().items || []);
    } catch (error) {
      setMessage('Error: ' + error.message);
    }
  };

  if (!playerId) return <p>Please log in.</p>;

  const sports = [...new Set(titles.map(t => t.sport))];
  const sortedTitles = titles.sort((a, b) => a.level - b.level);
  const selectedTitle = titles.find(t => t._id === selectedTitleId);
  const selectedDog = ownedDogs.find(d => d.dogId === dogId);

  const getRequirements = (title) => {
    const defaultRequirements = {
      allowedBreeds: [],
      requiredQS: 0,
      requiredArtworkLinks: 0,
      minAwardPoints: 0,
      prerequisites: [],
      requiredItems: [],
      customRequirements: ''
    };

    if (!title || !title.requirements || typeof title.requirements === 'string') {
      return {
        ...defaultRequirements,
        customRequirements: title?.requirements || ''
      };
    }

    return {
      ...defaultRequirements,
      ...title.requirements
    };
  };

  const hasPrerequisite = (title, dog) => {
    const reqs = getRequirements(title);
    if (!reqs.prerequisites.length) return { met: true, details: [] };
  
    const validPrerequisites = reqs.prerequisites.filter(prereq => typeof prereq === 'string' && prereq);
    if (!validPrerequisites.length) return { met: true, details: [] };
  
    const earnedTitleIds = new Set(dog.earnedTitles.map(t => t.titleId.toString()));
    const availableTitles = new Set(earnedTitleIds); // Track titles available for use

    const details = validPrerequisites.map(prereq => {
      const options = prereq.split('|');
      let usedTitle = null;
      const isMet = options.some(option => {
        const titleDef = titles.find(t => 
          t._id.toString() === option || 
          t.name === option || 
          t.prefixSuffix === option
        );
        if (!titleDef) return false;
        const titleId = titleDef._id.toString();
        if (earnedTitleIds.has(titleId) && availableTitles.has(titleId)) {
          usedTitle = titleDef;
          availableTitles.delete(titleId); // Remove from available
          return true;
        }
        return false;
      });
      return { prereq, isMet, usedTitle };
    });

    return { 
      met: details.every(d => d.isMet), 
      details,
      usedCount: details.filter(d => d.isMet).length
    };
  };

  const getTitleInstanceCount = (title, dog) => {
    return dog?.earnedTitles?.filter(et => et.titleId === title._id.toString()).length || 0;
  };

  const getDisplayPrefix = (title, instance) => {
    return title.prefixSuffix?.endsWith('#') ? title.prefixSuffix.replace('#', instance) : title.prefixSuffix;
  };

  const applicableItems = inventory.filter(item => {
    if (!selectedTitle) {
      console.log('No selected title, skipping all items');
      return false;
    }
    const modifiers = item.titleModifiers || {};
    const matchesSport = !modifiers.sport || modifiers.sport === selectedTitle.category;
    const hasBonus = modifiers.skipBreedRestriction || 
                     (modifiers.reduceQS > 0) || 
                     (modifiers.reduceArtworkLinks > 0) || 
                     (modifiers.reduceAwardPoints > 0);
    console.log(`Item: ${item.itemId}, Name: ${item.name}, Quantity: ${item.quantity}, Item Sport: ${modifiers.sport}, Title Category: ${selectedTitle.category}, Title Sport: ${selectedTitle.sport}, MatchesSport: ${matchesSport}, HasBonus: ${hasBonus}`);
    return item.quantity > 0 && matchesSport && hasBonus;
  });
  
  const renderStatus = (condition) => (
    <span style={{ color: condition ? '#4CAF50' : '#FF4D4D', marginLeft: '5px' }}>
      {condition ? '✓' : '✗'}
    </span>
  );

  const reqLinks = getRequirements(selectedTitle)?.requiredArtworkLinks || 0;
  const reducedLinks = selectedItemIds.reduce((sum, id) => sum + (inventory.find(i => i.itemId === id)?.titleModifiers?.reduceArtworkLinks || 0), 0);
  const neededLinks = Math.max(0, reqLinks - reducedLinks);

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1 style={{ textAlign: 'center', color: '#1E88E5' }}>Titles System</h1>
      {message && (
        <p style={{ color: message.includes('Error') ? 'red' : 'green', textAlign: 'center' }}>{message}</p>
      )}

      <div style={{ background: '#f9f9f9', padding: '15px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h2 style={{ color: '#4CAF50', marginTop: 0 }}>Submit Titles</h2>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Dog</label>
          <select value={dogId} onChange={e => setDogId(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc' }}>
            <option value="">Select a Dog</option>
            {ownedDogs.map(dog => (
              <option key={dog.dogId} value={dog.dogId}>{dog.registeredName} ({dog.dogId})</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Sport</label>
          <select 
            value={selectedSport} 
            onChange={e => {
              setSelectedSport(e.target.value);
              setSelectedTitleId('');
            }} 
            style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc' }}
          >
            <option value="">Select a Sport</option>
            {sports.map(sport => (
              <option key={sport} value={sport}>{sport}</option>
            ))}
          </select>
        </div>
        {selectedSport && (
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Title</label>
            <select 
              value={selectedTitleId} 
              onChange={e => setSelectedTitleId(e.target.value)} 
              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc' }}
            >
              <option value="">Select a Title</option>
              {sortedTitles.filter(t => t.sport === selectedSport).map(t => {
                const isRepeatable = t.prefixSuffix?.endsWith('#') || false;
                const instanceCount = selectedDog ? getTitleInstanceCount(t, selectedDog) : 0;
                const nextInstance = instanceCount + 1;
                const displayPrefix = getDisplayPrefix(t, isRepeatable ? nextInstance : 1);
                const isDisabled = !isRepeatable && instanceCount > 0;
                return (
                  <option
                    key={t._id}
                    value={t._id}
                    disabled={isDisabled}
                    style={{ color: isDisabled ? '#888' : 'black' }}
                  >
                    {t.name} (Level {t.level}) - {displayPrefix} {instanceCount > 0 ? `(Earned ${instanceCount} time${instanceCount > 1 ? 's' : ''})` : ''}
                  </option>
                );
              })}
            </select>
          </div>
        )}
        {selectedTitle && selectedDog && (
          <div style={{ marginBottom: '15px', background: '#fff', padding: '10px', borderRadius: '5px' }}>
            <p><strong>Requirements:</strong></p>
            <ul style={{ listStyle: 'none', padding: '0' }}>
              {getRequirements(selectedTitle).allowedBreeds.length > 0 && (
                <li>
                  Allowed Breeds:{' '}
                  {(() => {
                    const skipBreed = selectedItemIds.some(id => inventory.find(i => i.itemId === id)?.titleModifiers?.skipBreedRestriction);
                    const breedMatch = getRequirements(selectedTitle).allowedBreeds.some(breed => selectedDog.breed.toLowerCase().includes(breed.toLowerCase()));
                    const displayBreed = getRequirements(selectedTitle).allowedBreeds.join(', ');
                    return (
                      <>
                        <span style={{ color: skipBreed || breedMatch ? '#4CAF50' : '#FF4D4D' }}>
                          {displayBreed}
                        </span>
                        {renderStatus(skipBreed || breedMatch)}
                        {skipBreed && <span style={{ color: '#666', marginLeft: '5px' }}>(Skipped with Item)</span>}
                      </>
                    );
                  })()}
                </li>
              )}
              <li>
                Required QS:{' '}
                {(() => {
                  const reqQS = getRequirements(selectedTitle).requiredQS;
                  const reducedQS = selectedItemIds.reduce((sum, id) => {
                    const item = inventory.find(i => i.itemId === id);
                    const modifiers = item?.titleModifiers || {};
                    const sportMatch = !modifiers.sport || modifiers.sport === selectedTitle.category;
                    return sum + (sportMatch ? (modifiers.reduceQS || 0) : 0);
                  }, 0);
                  const neededQS = Math.max(0, reqQS - reducedQS);
                  const hasQS = selectedDog.totalUsableQualifyingScores >= neededQS;
                  return (
                    <>
                      {neededQS} (Dog has {selectedDog.totalUsableQualifyingScores})
                      {renderStatus(hasQS)}
                      {reducedQS > 0 && ` (Reduced by ${reducedQS})`}
                    </>
                  );
                })()}
              </li>
              <li>
                Required Artwork Links:{' '}
                {(() => {
                  const reqLinks = getRequirements(selectedTitle).requiredArtworkLinks;
                  const reducedLinks = selectedItemIds.reduce((sum, id) => sum + (inventory.find(i => i.itemId === id)?.titleModifiers?.reduceArtworkLinks || 0), 0);
                  const neededLinks = Math.max(0, reqLinks - reducedLinks);
                  const hasLinks = proofUrls.filter(url => url.trim() !== '').length >= neededLinks;
                  return (
                    <>
                      {neededLinks}
                      {reqLinks > 0 && ` (Items reduce by ${reducedLinks})`}
                      {renderStatus(neededLinks === 0 || hasLinks)}
                    </>
                  );
                })()}
              </li>
              {getRequirements(selectedTitle).minAwardPoints > 0 && (
                <li>
                  Min Award Points:{' '}
                  {(() => {
                    const reqPoints = getRequirements(selectedTitle).minAwardPoints;
                    const reducedPoints = selectedItemIds.reduce((sum, id) => sum + (inventory.find(i => i.itemId === id)?.titleModifiers?.reduceAwardPoints || 0), 0);
                    const neededPoints = Math.max(0, reqPoints - reducedPoints);
                    const totalPoints = selectedDog.sportingRecords?.reduce((sum, r) => sum + (r.awardPoints || 0), 0) || 0;
                    return (
                      <>
                        {neededPoints} (Dog has {totalPoints})
                        {renderStatus(totalPoints >= neededPoints)}
                      </>
                    );
                  })()}
                </li>
              )}
              {getRequirements(selectedTitle).prerequisites.length > 0 && (
                <li>
                  Prerequisites:
                  <div style={{ marginLeft: '20px' }}>
                    {(() => {
                      const { details, usedCount } = hasPrerequisite(selectedTitle, selectedDog);
                      const validPrerequisites = getRequirements(selectedTitle).prerequisites.filter(prereq => typeof prereq === 'string' && prereq);
                      const reqCount = validPrerequisites.length;
                      const earnedTitleIds = new Set(selectedDog.earnedTitles.map(t => t.titleId.toString()));

                      return details.map((d, i) => {
                        const options = d.prereq.split('|');
                        const renderedOptions = options.map((option, j) => {
                          const titleDef = titles.find(t => 
                            t._id.toString() === option || 
                            t.name === option || 
                            t.prefixSuffix === option
                          );
                          const isEarned = titleDef && earnedTitleIds.has(titleDef._id.toString());
                          const isUsed = d.usedTitle && titleDef && d.usedTitle._id.toString() === titleDef._id.toString();
                          return (
                            <span 
                              key={option} 
                              style={{ 
                                color: isUsed ? '#4CAF50' : (isEarned ? '#888' : '#FF4D4D')
                              }}
                            >
                              {option}
                            </span>
                          );
                        }).reduce((prev, curr, idx) => idx === 0 ? [curr] : [...prev, ' or ', curr], []);

                        return (
                          <div key={i} style={{ marginBottom: '5px' }}>
                            {renderedOptions}
                            {d.isMet && d.usedTitle && <span style={{ color: '#4CAF50', marginLeft: '5px' }}>(Met: {d.usedTitle.prefixSuffix})</span>}
                          </div>
                        );
                      }).concat(
                        usedCount < reqCount ? 
                          [<div key="extra" style={{ color: '#FF4D4D' }}>Need {reqCount - usedCount} more title{reqCount - usedCount > 1 ? 's' : ''}</div>] 
                          : []
                      );
                    })()}
                    {renderStatus(hasPrerequisite(selectedTitle, selectedDog).met)}
                  </div>
                </li>
              )}
              {getRequirements(selectedTitle).customRequirements && (
                <li>
                  Additional:
                  <div style={{ marginLeft: '20px' }}>
                    {getRequirements(selectedTitle).customRequirements
                      .replace(/\r\n/g, '\n')
                      .replace(/\r/g, '\n')
                      .split('\n')
                      .map((line, index) => (
                        <div key={index}>{line.trim()}</div>
                      ))}
                  </div>
                </li>
              )}
            </ul>
          </div>
        )}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Proof URLs</label>
          {selectedTitle ? (
            Array.from({ length: neededLinks || 1 }, (_, i) => (
              <input
                key={i}
                value={proofUrls[i] || ''}
                onChange={e => {
                  const newUrls = [...proofUrls];
                  newUrls[i] = e.target.value;
                  setProofUrls(newUrls);
                }}
                placeholder={`Proof URL #${i + 1}`}
                style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', marginBottom: '5px' }}
              />
            ))
          ) : (
            <input
              value={proofUrls[0] || ''}
              onChange={e => setProofUrls([e.target.value])}
              placeholder="Proof URL #1"
              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc' }}
            />
          )}
        </div>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Apply Items (Ctrl+click to select/deselect)</label>
          {applicableItems.length > 0 ? (
            <>
              <select 
                multiple 
                value={selectedItemIds} 
                onChange={e => setSelectedItemIds(Array.from(e.target.selectedOptions, option => option.value))} 
                style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', minHeight: '100px' }}
              >
                {applicableItems.map(item => (
                  <option key={item.itemId} value={item.itemId}>
                    {item.name} (Qty: {item.quantity}) 
                    {item.titleModifiers?.reduceQS > 0 ? ` - Reduces QS by ${item.titleModifiers.reduceQS}` : ''} 
                    {item.titleModifiers?.reduceArtworkLinks > 0 ? ` - Reduces Artwork by ${item.titleModifiers.reduceArtworkLinks}` : ''}
                  </option>
                ))}
              </select>
              <button 
                onClick={() => setSelectedItemIds([])} 
                style={{ marginTop: '5px', backgroundColor: '#ff4d4d', color: 'white', padding: '5px 10px', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
              >
                Clear Selected Items
              </button>
            </>
          ) : (
            <p style={{ color: '#666' }}>No applicable items available.</p>
          )}
        </div>
        <button onClick={submitTitle} style={{ backgroundColor: '#4CAF50', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '5px', cursor: 'pointer', width: '100%' }}>
          Submit Title
        </button>
      </div>

      <div style={{ background: '#f9f9f9', padding: '15px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h2 style={{ color: '#4CAF50', marginTop: 0 }}>My Pending Titles</h2>
        {pendingTitles.length > 0 ? (
          pendingTitles.map((title, index) => {
            const titleDef = titles.find(td => td._id.toString() === title.titleId) || {};
            return (
              <div key={title._id} style={{ marginBottom: '20px', borderBottom: index < pendingTitles.length - 1 ? '1px solid #ccc' : 'none', paddingBottom: '10px' }}>
                <p>Dog: <Link to={`/dog/${title.dogId}`}>{title.dogId}</Link></p>
                <p>Title: {titleDef.sport} - {titleDef.name} ({titleDef.prefixSuffix})</p>
                <p>Status: {title.status}</p>
                {title.artworkUrls && title.artworkUrls.length > 0 && (
                  <p>Proof: {title.artworkUrls.map((url, i) => (
                    <span key={i}><a href={url} target="_blank" rel="noopener noreferrer">{url}</a>{i < title.artworkUrls.length - 1 ? ', ' : ''}</span>
                  ))}</p>
                )}
                {title.usedItemIds?.length > 0 && <p>Used Items: {title.usedItemIds.join(', ')}</p>}
                {title.status === 'Denied' && (
                  <>
                    <p style={{ color: 'red' }}>Denied: {title.denyReason}</p>
                    <button onClick={() => clearDeniedTitle(title._id)} style={{ background: '#ff4d4d', color: 'white', padding: '5px 10px', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
                      Clear Notice
                    </button>
                  </>
                )}
              </div>
            );
          })
        ) : (
          <p>No pending or denied titles.</p>
        )}
      </div>
    </div>
  );
}

export default TitlesSystem;